/**
 * Documents API
 * Handles document upload, management, status updates, and file operations
 */

import {
  apiClient,
  handleApiResponse,
  handleApiError,
  createFormDataConfig,
  createDownloadConfig,
} from "../config";
import type {
  Document,
  DocumentCreate,
  DocumentUpdate,
  DocumentFilter,
  DocumentStats,
  UploadResponse,
  DocumentDownload,
  StatusUpdate,
  PaginatedResponse,
} from "../../types";

export class DocumentsAPI {
  /**
   * Upload documents
   */
  static async upload(
    files: FileList | File[],
    shopkeeperID: number,
    metadata: Partial<DocumentCreate> = {}
  ): Promise<UploadResponse[]> {
    try {
      const formData = new FormData();

      // Add files
      Array.from(files).forEach((file) => {
        formData.append(`files`, file);
      });

      // Add metadata
      formData.append("shopkeeper_id", shopkeeperID.toString());
      if (metadata.sender_name)
        formData.append("sender_name", metadata.sender_name);
      if (metadata.sender_phone)
        formData.append("sender_phone", metadata.sender_phone);
      if (metadata.sender_email)
        formData.append("sender_email", metadata.sender_email);
      if (metadata.sender_message)
        formData.append("sender_message", metadata.sender_message);
      if (metadata.special_instructions)
        formData.append("special_instructions", metadata.special_instructions);
      if (metadata.print_copies)
        formData.append("print_copies", metadata.print_copies.toString());
      if (metadata.print_color)
        formData.append("print_color", metadata.print_color);
      if (metadata.paper_size)
        formData.append("paper_size", metadata.paper_size);
      if (metadata.is_urgent !== undefined)
        formData.append("is_urgent", metadata.is_urgent.toString());

      const response = await apiClient.post(
        "/api/v1/documents/upload/",
        formData,
        createFormDataConfig()
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get document by ID
   */
  static async getDocument(documentId: number): Promise<Document> {
    try {
      const response = await apiClient.get(`/api/v1/documents/${documentId}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get documents list with filtering
   */
  static async getDocuments(
    filter: DocumentFilter = {}
  ): Promise<PaginatedResponse<Document>> {
    try {
      const params = new URLSearchParams();

      if (filter.status?.length)
        params.append("status", filter.status.join(","));
      if (filter.sender_type) params.append("sender_type", filter.sender_type);
      if (filter.date_from) params.append("date_from", filter.date_from);
      if (filter.date_to) params.append("date_to", filter.date_to);
      if (filter.is_urgent !== undefined)
        params.append("is_urgent", filter.is_urgent.toString());
      if (filter.search_term) params.append("search", filter.search_term);

      const response = await apiClient.get(
        `/api/v1/documents?${params.toString()}`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update document
   */
  static async updateDocument(
    documentId: number,
    updates: DocumentUpdate
  ): Promise<Document> {
    try {
      const response = await apiClient.put(
        `/api/v1/documents/${documentId}`,
        updates
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update document status
   */
  static async updateStatus(
    documentId: number,
    status: string,
    notes?: string
  ): Promise<StatusUpdate> {
    try {
      const response = await apiClient.post(
        `/api/v1/documents/${documentId}/status`,
        {
          status,
          notes,
        }
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Mark document as printed
   */
  static async markPrinted(
    documentId: number,
    finalCost?: number
  ): Promise<StatusUpdate> {
    try {
      const response = await apiClient.post(
        `/api/v1/documents/${documentId}/mark-printed`,
        {
          final_cost: finalCost,
        }
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Confirm document receipt (by sender)
   */
  static async confirmReceipt(
    confirmationToken: string
  ): Promise<StatusUpdate> {
    try {
      const response = await apiClient.post(
        `/api/v1/documents/confirm/${confirmationToken}`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get document download URL
   */
  static async getDownloadUrl(documentId: number): Promise<DocumentDownload> {
    try {
      const response = await apiClient.get(
        `/api/v1/documents/${documentId}/download`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Download document file
   */
  static async downloadFile(documentId: number): Promise<Blob> {
    try {
      const response = await apiClient.get(
        `/api/v1/documents/${documentId}/file`,
        createDownloadConfig()
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete document
   */
  static async deleteDocument(documentId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/documents/${documentId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get document statistics
   */
  static async getStats(shopkeeperId?: number): Promise<DocumentStats> {
    try {
      const params = shopkeeperId ? `?shopkeeper_id=${shopkeeperId}` : "";
      const response = await apiClient.get(`/api/v1/documents/stats/${params}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get documents grouped by sender
   */
  static async getDocumentGroups(shopkeeperId: number): Promise<any[]> {
    try {
      const response = await apiClient.get(
        `/api/v1/documents/groups/?shopkeeper_id=${shopkeeperId}`
      );
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Bulk update documents
   */
  static async bulkUpdate(
    documentIds: number[],
    updates: DocumentUpdate
  ): Promise<StatusUpdate[]> {
    try {
      const response = await apiClient.post("/api/v1/documents/bulk-update/", {
        document_ids: documentIds,
        updates,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Export default instance for convenience
export const documentsAPI = DocumentsAPI;
